import React, { useCallback, useEffect, useState } from 'react';
import { BadgeCheck, CalendarClock, Gift, PauseCircle, Pencil, PlayCircle, Repeat2 } from 'lucide-react';

// CP-111 (P2): the store's day-zero loyalty program, managed right here —
// it exists from the moment the store does, and the merchant admin (or the
// Zopeo salesman sitting with them) can tune it. Pause is the escape hatch:
// stamping and minting stop; earned rewards are never voided. Mid-cycle edits
// grandfather automatically — cards snapshot their target at cycle start.
const REWARD_LABELS = {
    PERCENT_CAPPED: (p) => `${p.rewardValue}% off${p.rewardCapAmount ? ` (max ₹${p.rewardCapAmount})` : ''}`,
    FLAT_AMOUNT: (p) => `₹${p.rewardValue} off`,
    FREE_SERVICE: () => 'a free service'
};

const LoyaltyProgramCard = ({ request, onToast }) => {
    const [program, setProgram] = useState(null);
    const [isMutating, setIsMutating] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(null);

    const load = useCallback(async () => {
        try {
            const response = await request('/v1/admin/loyalty/program', {
                method: 'GET',
                requestName: 'loyalty-program-view'
            });
            setProgram(response?.data || null);
        } catch (error) {
            setProgram(null); // additive card — a failed fetch just hides it
        }
    }, [request]);

    useEffect(() => { load(); }, [load]);

    const togglePause = async () => {
        if (!program || isMutating) return;
        setIsMutating(true);
        try {
            const response = await request('/v1/admin/loyalty/program/pause', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                requestName: 'loyalty-program-pause',
                body: JSON.stringify({ paused: !program.paused })
            });
            setProgram(response?.data || program);
            onToast?.({
                message: !program.paused
                    ? 'Loyalty program paused — visits will not stamp until you resume'
                    : 'Loyalty program resumed',
                variant: 'success'
            });
        } catch (error) {
            onToast?.({ message: error?.data?.message || 'Could not update the loyalty program.', variant: 'error' });
        } finally {
            setIsMutating(false);
        }
    };

    // CP-111 (P2 revision): edits happen HERE — by the merchant admin or the
    // Zopeo salesman sitting with them (STORE_ADMIN/ZOPEO_ADMIN on the API).
    const startEdit = () => {
        setDraft({
            visitsRequired: program.visitsRequired,
            rewardType: program.rewardType || 'PERCENT_CAPPED',
            rewardValue: program.rewardValue,
            rewardCapAmount: program.rewardCapAmount || '',
            rewardExpiryDays: program.rewardExpiryDays
        });
        setIsEditing(true);
    };

    const saveEdit = async () => {
        if (!draft || isMutating) return;
        setIsMutating(true);
        try {
            const response = await request('/v1/admin/loyalty/program', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                requestName: 'loyalty-program-update',
                body: JSON.stringify({
                    visitsRequired: Number(draft.visitsRequired),
                    rewardType: draft.rewardType,
                    rewardValue: Number(draft.rewardValue),
                    rewardCapAmount: draft.rewardCapAmount === '' ? null : Number(draft.rewardCapAmount),
                    rewardExpiryDays: Number(draft.rewardExpiryDays)
                })
            });
            setProgram(response?.data || program);
            setIsEditing(false);
            onToast?.({ message: 'Loyalty program updated — in-progress cards keep their current target', variant: 'success' });
        } catch (error) {
            onToast?.({ message: error?.data?.message || 'Could not update the loyalty program.', variant: 'error' });
        } finally {
            setIsMutating(false);
        }
    };

    if (!program) return null;
    const rewardLabel = (REWARD_LABELS[program.rewardType] || (() => 'a reward'))(program);
    const visitTarget = Number(program.visitsRequired) || 0;
    const stampCount = Math.min(Math.max(visitTarget, 1), 8);
    const hasOverflowStamps = visitTarget > stampCount;

    return (
        <article className={`offers-kit-card loyalty-program-card ${program.paused ? 'paused' : 'live'}`}>
            <div className="loyalty-program-head">
                <div>
                    <p className="loyalty-program-eyebrow">Retention engine</p>
                    <h3>Loyalty program</h3>
                    <p className={`loyalty-program-status ${program.paused ? 'paused' : 'live'}`}>
                        <BadgeCheck size={14} aria-hidden="true" />
                        {program.paused ? 'Paused' : 'Live for every customer'}
                    </p>
                </div>
                <div className="loyalty-program-actions">
                    {!isEditing ? (
                        <button type="button" className="btn ghost small" onClick={startEdit} disabled={isMutating}>
                            <Pencil size={13} aria-hidden="true" /> Edit
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="btn ghost small"
                        onClick={togglePause}
                        disabled={isMutating}
                    >
                        {program.paused ? <PlayCircle size={13} aria-hidden="true" /> : <PauseCircle size={13} aria-hidden="true" />}
                        {program.paused ? 'Resume' : 'Pause'}
                    </button>
                </div>
            </div>

            <div className="loyalty-program-overview">
                <div className="loyalty-program-rule">
                    <span>Reward rule</span>
                    <strong>{`Every ${program.visitsRequired}th billed visit`}</strong>
                    <p>{`Earns ${rewardLabel}. Rewards expire ${program.rewardExpiryDays} days after they are earned.`}</p>
                </div>
                <div className="loyalty-program-metrics" aria-label="Loyalty program settings">
                    <div className="loyalty-program-metric">
                        <Repeat2 size={16} aria-hidden="true" />
                        <span>Visit target</span>
                        <strong>{program.visitsRequired}</strong>
                    </div>
                    <div className="loyalty-program-metric">
                        <Gift size={16} aria-hidden="true" />
                        <span>Reward</span>
                        <strong>{rewardLabel}</strong>
                    </div>
                    <div className="loyalty-program-metric">
                        <CalendarClock size={16} aria-hidden="true" />
                        <span>Expiry</span>
                        <strong>{program.rewardExpiryDays} days</strong>
                    </div>
                </div>
            </div>

            <div className="loyalty-program-stamp-row" aria-label={`${program.visitsRequired} visit loyalty cycle`}>
                {Array.from({ length: stampCount }, (_, index) => {
                    const stampNumber = index + 1;
                    const isReward = stampNumber === stampCount && !hasOverflowStamps;
                    return (
                        <span
                            key={stampNumber}
                            className={`loyalty-program-stamp ${isReward ? 'reward' : ''}`}
                            aria-label={isReward ? `Visit ${stampNumber}, reward earned` : `Visit ${stampNumber}`}
                        >
                            {isReward ? <Gift size={13} aria-hidden="true" /> : stampNumber}
                        </span>
                    );
                })}
                {hasOverflowStamps ? (
                    <>
                        <span className="loyalty-program-stamp overflow" aria-label="Additional visits">...</span>
                        <span className="loyalty-program-stamp reward" aria-label={`Visit ${program.visitsRequired}, reward earned`}>
                            <Gift size={13} aria-hidden="true" />
                        </span>
                    </>
                ) : null}
            </div>

            {isEditing && draft ? (
                <div className="loyalty-program-edit">
                    <label>
                        Visits required
                        <input type="number" min="2" max="20" value={draft.visitsRequired}
                            onChange={(e) => setDraft({ ...draft, visitsRequired: e.target.value })} />
                    </label>
                    <label>
                        Reward type
                        <select value={draft.rewardType}
                            onChange={(e) => setDraft({ ...draft, rewardType: e.target.value })}>
                            <option value="PERCENT_CAPPED">% off (capped)</option>
                            <option value="FLAT_AMOUNT">Flat ₹ off</option>
                            <option value="FREE_SERVICE">Free service</option>
                        </select>
                    </label>
                    {draft.rewardType !== 'FREE_SERVICE' ? (
                        <label>
                            {draft.rewardType === 'PERCENT_CAPPED' ? '% off' : '₹ off'}
                            <input type="number" min="1" value={draft.rewardValue}
                                onChange={(e) => setDraft({ ...draft, rewardValue: e.target.value })} />
                        </label>
                    ) : null}
                    {draft.rewardType === 'PERCENT_CAPPED' ? (
                        <label>
                            Max ₹ cap
                            <input type="number" min="1" value={draft.rewardCapAmount}
                                onChange={(e) => setDraft({ ...draft, rewardCapAmount: e.target.value })} />
                        </label>
                    ) : null}
                    <label>
                        Reward expiry (days)
                        <input type="number" min="7" max="365" value={draft.rewardExpiryDays}
                            onChange={(e) => setDraft({ ...draft, rewardExpiryDays: e.target.value })} />
                    </label>
                    <div className="loyalty-program-edit-actions">
                        <button type="button" className="btn primary small" onClick={saveEdit} disabled={isMutating}>
                            {isMutating ? 'Saving…' : 'Save changes'}
                        </button>
                        <button type="button" className="btn ghost small" onClick={() => setIsEditing(false)} disabled={isMutating}>
                            Cancel
                        </button>
                    </div>
                    <p className="loyalty-program-footnote">
                        Changes apply to new cycles. Customers mid-card keep the target they started with.
                    </p>
                </div>
            ) : (
                <p className="loyalty-program-footnote">
                    Stamps come from billed visits automatically. Earned rewards are kept even if the program is paused.
                </p>
            )}
        </article>
    );
};

export default LoyaltyProgramCard;
